import { logger, enhancedLogger } from '@/utils/logger' // Enhanced logger import
import { auth } from '@/utils/firebaseConfig'
import { paramKeyForArray } from '@/utils/api-params'
import { csrfManager } from '@/utils/csrf-manager'
import { etagManager } from '@/utils/etag-manager'
import { isAuthenticated, getSafeAuthToken } from '@/utils/auth-helpers'
import type { SecureRequestConfig, SecurityHeaders } from '@/types/api/security'

type ApiResponse<T> = Promise<T>

interface RequestConfig {
  params?: Record<string, unknown>
  body?: unknown
  signal?: AbortSignal
  headers?: Record<string, string>
}

interface ApiError extends Error {
  status?: number
  code?: string
  details?: Record<string, unknown>
}

interface ApiFetcher {
  get: <T>(path: string, config?: RequestConfig) => ApiResponse<T>
  post: <T>(path: string, body?: unknown, config?: RequestConfig) => ApiResponse<T>
  put: <T>(path: string, body?: unknown, config?: RequestConfig) => ApiResponse<T>
  patch: <T>(path: string, body?: unknown, config?: RequestConfig) => ApiResponse<T> // Add patch method
  delete: <T>(path: string, config?: RequestConfig) => ApiResponse<T>
}

// セキュリティ強化されたApiFetcher（後方互換性維持）
interface SecureApiFetcher extends ApiFetcher {
  secureGet: <T>(path: string, config?: SecureRequestConfig) => ApiResponse<T>
  securePost: <T>(path: string, body?: unknown, config?: SecureRequestConfig) => ApiResponse<T>
  securePut: <T>(path: string, body?: unknown, config?: SecureRequestConfig) => ApiResponse<T>
  securePatch: <T>(path: string, body?: unknown, config?: SecureRequestConfig) => ApiResponse<T>
  secureDelete: <T>(path: string, config?: SecureRequestConfig) => ApiResponse<T>
}

const BASE_URL = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:8080/api'

async function handleResponse<T>(
  response: Response,
  method: string,
  resourceKey?: string
): Promise<T> {
  const contentType = response.headers.get('content-type')
  logger.debug('API レスポンス処理開始:', {
    url: response.url,
    status: response.status,
    contentType: contentType, // Log Content-Type
    statusText: response.statusText,
    ok: response.ok,
    headers: Object.fromEntries(
      [...response.headers.entries()].filter(
        ([key]) => !['authorization', 'cookie'].includes(key.toLowerCase())
      )
    ),
  })

  // ETagが存在する場合はキャッシュ
  if (resourceKey) {
    etagManager.extractAndCacheETag(response, resourceKey)
  }

  // ステータスコード 204 No Content の場合は、ボディを読まずにデフォルト値を返す
  if (response.status === 204) {
    logger.debug('API レスポンス 204 No Content、デフォルト値を返します。')
    // API仕様に基づいて適切なデフォルト値を返す (ここでは空オブジェクト)
    return {} as T
  }

  let text: string
  try {
    text = await response.text()
    logger.debug('API レスポンス生テキスト取得:', {
      length: text.length,
      start: text.substring(0, 100) + (text.length > 100 ? '...' : ''), // Log beginning of text
    })
  } catch (err) {
    logger.error('レスポンステキスト取得エラー:', {
      url: response.url,
      status: response.status,
      error: err,
    })

    // Don't send 410 errors to Sentry - they're expected behavior
    if (response.status !== 410) {
      // Sentry APIエラー記録
      enhancedLogger.apiError(method, response.url, response.status, err as Error)
    }

    throw new Error(`Failed to get response text: ${err}`)
  }

  // テキストが空の場合はパースせずにデフォルト値を返す
  if (!text.trim()) {
    logger.debug('API レスポンスボディが空、デフォルト値を返します。', {
      url: response.url,
      status: response.status,
    })
    // ステータス 204 と同様に適切なデフォルト値を返す
    return {} as T
  }

  let jsonResponse
  try {
    logger.debug('JSON パース試行:', { url: response.url, status: response.status }) // Log before parsing
    jsonResponse = JSON.parse(text)
    logger.debug(
      'API レスポンスJSONパース成功:', // Log success
      jsonResponse
        ? {
            message: jsonResponse.message,
            hasResults: !!jsonResponse.results,
            resultsType: jsonResponse.results
              ? Array.isArray(jsonResponse.results)
                ? 'array'
                : 'object'
              : 'none',
            resultsLength:
              jsonResponse.results && Array.isArray(jsonResponse.results)
                ? jsonResponse.results.length
                : 'N/A',
            error: jsonResponse.error,
          }
        : 'null'
    )
  } catch (parseErr) {
    logger.error('JSONパースエラー:', {
      url: response.url,
      status: response.status,
      contentType: contentType,
      error: parseErr,
      responseText: text.substring(0, 500) + (text.length > 500 ? '...' : ''), // Log failing text
    })

    // Don't send 410 errors to Sentry - they're expected behavior
    if (response.status !== 410) {
      // Sentry APIエラー記録
      enhancedLogger.apiError(method, response.url, response.status, parseErr as Error)
    }

    // HTMLレスポンスの場合、内容を詳細に表示
    const isHTML =
      text.trim().toLowerCase().startsWith('<!doctype') ||
      text.trim().toLowerCase().startsWith('<html') ||
      contentType?.includes('text/html')

    if (isHTML) {
      // HTMLから意味のある情報を抽出
      const titleMatch = text.match(/<title>(.*?)<\/title>/i)
      const h1Match = text.match(/<h1[^>]*>(.*?)<\/h1>/i)

      const errorDetails = {
        url: response.url,
        status: response.status,
        statusText: response.statusText,
        contentType: contentType,
        pageTitle: titleMatch ? titleMatch[1] : 'Unknown',
        pageHeading: h1Match ? h1Match[1] : 'Unknown',
        htmlPreview: text.substring(0, 1000),
        fullHTML: text,
      }

      logger.error('🔴 [API] HTMLレスポンスが返されました（JSONを期待）:', errorDetails)

      throw new Error(
        `APIがHTMLを返しました。Status: ${response.status} ${response.statusText}. ` +
          `Title: "${errorDetails.pageTitle}". ` +
          `これは通常、404エラー、認証エラー、またはサーバーエラーを示します。` +
          `\nURL: ${response.url}` +
          `\nHTML Preview: ${errorDetails.htmlPreview}`
      )
    }

    throw new Error(
      `Failed to parse response as JSON. Status: ${response.status}. Error: ${parseErr}. ` +
        `Response: ${text.substring(0, 500)}`
    )
  }

  if (!response.ok) {
    // 410 Gone responses should be logged as info, not error
    // This is expected behavior when batch jobs are cleaned up after completion
    if (response.status === 410) {
      logger.info('API リソース削除済み (410 Gone):', {
        status: response.status,
        message: jsonResponse?.message,
        errorCode: jsonResponse?.error?.code,
        url: response.url,
        explanation: 'Resource has been cleaned up - this is normal for completed batch jobs',
      })
    } else {
      logger.error('API エラーレスポンス:', {
        status: response.status,
        message: jsonResponse?.message,
        errorCode: jsonResponse?.error?.code,
        errorDetails: jsonResponse?.error?.details,
        fullResponse: jsonResponse, // デバッグ用：完全なレスポンスを記録
      })
    }

    // デバッグ: エラーメッセージの抽出をログ
    // エラーメッセージは jsonResponse.message または jsonResponse.error.message に存在する可能性がある
    // Try to surface useful validation details if present
    const validationMessages: string[] =
      (jsonResponse?.details?.errors && Array.isArray(jsonResponse.details.errors)
        ? jsonResponse.details.errors
            .map((e: Record<string, unknown>) => e?.message)
            .filter((m: unknown): m is string => typeof m === 'string' && m.trim().length > 0)
        : []) || []

    const errorMessage =
      (validationMessages.length > 0
        ? `${jsonResponse?.message || 'Request validation failed'}: ${validationMessages.join(', ')}`
        : jsonResponse?.message || jsonResponse?.error?.message) ||
      `HTTP error! status: ${response.status}`
    logger.error('🔴 エラーメッセージ抽出デバッグ:', {
      jsonResponseMessage: jsonResponse?.message,
      jsonResponseErrorMessage: jsonResponse?.error?.message,
      hasMessage: !!jsonResponse?.message,
      hasErrorMessage: !!jsonResponse?.error?.message,
      finalErrorMessage: errorMessage,
      responseStatus: response.status,
    })

    const error = new Error(errorMessage) as ApiError
    error.status = response.status
    error.code = jsonResponse?.code || jsonResponse?.error?.code
    // Preserve full details when provided either top-level or nested under error
    error.details = jsonResponse?.details || jsonResponse?.error?.details || jsonResponse?.error

    // Don't send 410 errors to Sentry - they're expected behavior
    if (response.status !== 410) {
      // Sentry APIエラー記録（エラーレスポンス）
      enhancedLogger.apiError(method, response.url, response.status, error)
    }

    // セキュリティエラーの特別処理
    if (
      response.status === 403 &&
      (jsonResponse?.message?.includes('CSRF') ||
        jsonResponse?.error?.code === 'CSRF_TOKEN_INVALID')
    ) {
      logger.warn('CSRF token error detected, clearing token cache')
      csrfManager.clearToken()

      // セキュリティ関連エラーの記録
      enhancedLogger.error('CSRF token error detected', error, {
        important: true,
        securityEvent: 'csrf_token_error',
      })
    } else if (response.status === 412) {
      logger.warn('ETag conflict detected', { resourceKey })
      if (resourceKey) {
        etagManager.clearETag(resourceKey)
      }

      enhancedLogger.warn('ETag conflict detected', {
        resourceKey,
        url: response.url,
      })
    }

    throw error
  }

  // Check if jsonResponse is an object and has 'results' property
  if (jsonResponse && typeof jsonResponse === 'object' && 'results' in jsonResponse) {
    logger.debug('API レスポンスから results フィールドを返します。', { url: response.url })
    return jsonResponse.results as T
  }

  // Special handling for analysis execution history API which uses 'data' instead of 'results'
  if (
    jsonResponse &&
    typeof jsonResponse === 'object' &&
    'data' in jsonResponse &&
    response.url &&
    response.url.includes('/analyses/batch/executions')
  ) {
    logger.debug('API レスポンスから data フィールドを返します（実行履歴API）。', {
      url: response.url,
    })
    return jsonResponse.data as T
  }

  // Handle cases where 'results' is not expected or response is not an object
  logger.warn(
    'API レスポンスに results フィールドがないか、予期しない形式です。レスポンス全体を返します。',
    { url: response.url, responseType: typeof jsonResponse, response: jsonResponse }
  )
  return jsonResponse as T
}

async function getAuthToken(retryCount: number = 0): Promise<string> {
  const maxRetries = 2

  try {
    logger.debug('認証トークン取得開始', {
      currentUser: !!auth.currentUser,
      isSignedIn: !!auth.currentUser?.uid,
      isAuthenticated: isAuthenticated(),
      retryCount,
    })

    // Firebase認証状態の詳細チェック
    if (!auth.currentUser) {
      logger.error('認証トークン取得失敗: Firebaseユーザーが存在しません')
      throw new Error('Firebase user not found - user may not be logged in')
    }

    if (!auth.currentUser.uid) {
      logger.error('認証トークン取得失敗: UIDが存在しません')
      throw new Error('Firebase user UID not found - invalid user state')
    }

    // インテリジェントなトークンリフレッシュ（必要な場合のみ更新）
    const forceRefresh = retryCount > 0 // リトライ時は強制更新
    const token = await getSafeAuthToken(forceRefresh)
    if (!token) {
      logger.error('認証トークン取得失敗: トークンが空です')
      throw new Error('Firebase token is empty or failed to refresh')
    }

    // トークンの有効性チェック
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const expiryTime = new Date(payload.exp * 1000)
      const now = new Date()

      logger.debug('認証トークン取得成功', {
        tokenLength: token.length,
        userEmail: auth.currentUser.email,
        tokenExpiry: expiryTime.toISOString(),
        timeUntilExpiry: `${Math.round((expiryTime.getTime() - now.getTime()) / 1000 / 60)}分`,
        isExpired: expiryTime <= now,
        retryCount,
        forceRefresh,
      })

      if (expiryTime <= now) {
        logger.warn('取得したトークンが既に期限切れです', {
          expiry: expiryTime.toISOString(),
          now: now.toISOString(),
        })
      }
    } catch (parseError) {
      logger.warn('トークンの詳細解析に失敗しましたが、トークンは有効です', {
        parseError: parseError instanceof Error ? parseError.message : String(parseError),
      })
    }

    return token
  } catch (error) {
    // トークン取得に失敗した場合、リトライを試行
    if (retryCount < maxRetries) {
      logger.warn(`認証トークン取得リトライ (${retryCount + 1}/${maxRetries + 1})`, {
        error: error instanceof Error ? error.message : String(error),
        retryCount,
      })

      // 短い遅延後にリトライ
      await new Promise(resolve => setTimeout(resolve, 100 * (retryCount + 1)))
      return getAuthToken(retryCount + 1)
    }

    logger.error('認証トークン取得エラー (最大リトライ到達):', {
      error: error instanceof Error ? error.message : String(error),
      currentUser: !!auth.currentUser,
      userEmail: auth.currentUser?.email,
      userUid: auth.currentUser?.uid,
      isAuthenticated: isAuthenticated(),
      retryCount,
    })
    throw new Error(
      `Authentication failed after ${maxRetries + 1} attempts: ${error instanceof Error ? error.message : String(error)}`
    )
  }
}

// セキュリティヘッダーを構築する関数
async function buildSecurityHeaders(
  method: string,
  config?: SecureRequestConfig,
  body?: unknown
): Promise<SecurityHeaders> {
  const headers: SecurityHeaders = {
    'Content-Type': 'application/json',
  }

  // Google AI Overviewの場合は日本語ヘッダーを設定
  if (body && typeof body === 'object' && body !== null) {
    const requestBody = body as Record<string, unknown>
    if (requestBody.model_provider === 'google-ai-overview') {
      headers['Accept-Language'] = 'ja-JP,ja;q=0.9'
      logger.debug('Google AI Overview用言語ヘッダー設定:', { language: 'ja-JP' })
    }
  }

  // 認証ヘッダー
  if (!config?.skipAuth) {
    try {
      const token = await getAuthToken()
      headers['Authorization'] = `Bearer ${token}`
      logger.debug('認証ヘッダー設定完了', {
        method,
        hasToken: !!token,
        tokenPrefix: token.substring(0, 20) + '...',
        skipAuth: config?.skipAuth || false,
      })
    } catch (error) {
      logger.error('認証ヘッダー設定失敗', {
        method,
        error: error instanceof Error ? error.message : String(error),
        skipAuth: config?.skipAuth || false,
        isAuthenticated: isAuthenticated(),
      })
      // 認証エラーの場合は即座に例外を投げる
      throw new Error(
        `Authentication required for ${method} request: ${error instanceof Error ? error.message : String(error)}`
      )
    }
  } else {
    logger.debug('認証ヘッダーをスキップ', { method, skipAuth: true })
  }

  // 状態変更メソッドの場合はCSRFトークンを追加
  const isStateChangingMethod = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())
  if (isStateChangingMethod && !config?.skipCSRF) {
    try {
      headers['X-CSRF-Token'] = await csrfManager.getToken()
      logger.debug('CSRFトークン設定完了', { method })
    } catch (error) {
      logger.error('CSRF token acquisition failed:', error)
      throw error
    }
  }

  // ETagが必要な場合はIf-Matchヘッダーを追加
  if (config?.requireETag && config?.resourceKey) {
    try {
      const ifMatchHeaders = etagManager.generateIfMatchHeaders(config.resourceKey)
      Object.assign(headers, ifMatchHeaders)
      logger.debug('ETagヘッダー設定完了', { method, resourceKey: config.resourceKey })
    } catch (error) {
      logger.error('ETag header generation failed:', error)
      throw error
    }
  }

  logger.debug('セキュリティヘッダー構築完了', {
    method,
    hasAuth: !!headers['Authorization'],
    hasCSRF: !!headers['X-CSRF-Token'],
    hasETag: !!headers['If-Match'],
  })

  return headers
}

// 統合されたfetch関数
async function secureRequest<T>(
  method: string,
  path: string,
  body?: unknown,
  config?: SecureRequestConfig,
  retryCount: number = 0
): ApiResponse<T> {
  const startTime = Date.now()
  const requestId = `${method}-${path}-${startTime}`
  const maxRetries = 1 // Only retry once for CSRF errors

  try {
    // API リクエスト開始ログ
    enhancedLogger.apiRequest(method, `${BASE_URL}${path}`, body)

    // 🔍 DEBUG: 実行履歴APIのパス問題をデバッグ
    if (path.includes('analyses/batch/executions')) {
      console.error('🔍 [API-FETCHER] DEBUG - Execution history API call:', {
        path,
        fullUrl: `${BASE_URL}${path}`,
        hasServices: path.includes('/services/'),
        pathSegments: path.split('/'),
      })
    }

    const url = new URL(`${BASE_URL}${path}`)

    // ngrok経由アクセス時のブラウザ警告ページをスキップ（ヘッダーはpreflight失敗するためクエリパラメータで渡す）
    if (BASE_URL.includes('ngrok')) {
      url.searchParams.set('ngrok-skip-browser-warning', 'true')
    }

    // GETパラメータの処理
    if (method === 'GET' && config?.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            // 特定の配列パラメータは[]サフィックス付きで送信（共通ユーティリティ使用）
            const paramKey = paramKeyForArray(key)

            // 配列の場合は各要素を個別に追加
            value.forEach(item => {
              url.searchParams.append(paramKey, String(item))
            })
          } else {
            url.searchParams.append(key, String(value))
          }
        }
      })
    }

    // セキュリティヘッダーの構築
    const headers = await buildSecurityHeaders(method, config, body)

    // カスタムヘッダーをマージ（RequestConfigのheadersを追加）
    if (config?.headers) {
      Object.assign(headers, config.headers)
    }

    // リクエストの実行
    const response = await fetch(url.toString(), {
      method,
      headers: headers as HeadersInit,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include', // セッション維持のため必須
      signal: config?.signal,
      // Avoid stale cached 304 when query filters change (e.g., language)
      cache: 'no-store',
    })

    const duration = Date.now() - startTime

    // API レスポンス成功ログ
    enhancedLogger.apiResponse(method, url.toString(), response.status)

    // パフォーマンス記録
    enhancedLogger.performance(`API ${method} ${path}`, duration, {
      status: response.status,
      contentType: response.headers.get('content-type'),
      requestId,
    })

    const result = await handleResponse<T>(response, method, config?.resourceKey)

    return result
  } catch (error) {
    // Check if this is a CSRF error that we should retry
    if (
      error instanceof Error &&
      error.message.includes('CSRF') &&
      retryCount < maxRetries &&
      !config?.skipCSRF
    ) {
      logger.warn(`CSRF error detected, retrying request (${retryCount + 1}/${maxRetries})`, {
        method,
        path,
        error: error.message,
      })

      // Clear the CSRF token to force refresh
      csrfManager.clearToken()

      // Wait longer before retry to ensure backend session is established
      // Cloud Run environments need more time for session validation
      const retryDelay = 500 * (retryCount + 1) // 500ms, 1000ms for exponential backoff
      await new Promise(resolve => setTimeout(resolve, retryDelay))

      // Retry the request
      return secureRequest<T>(method, path, body, config, retryCount + 1)
    }
    const duration = Date.now() - startTime

    // AbortErrorの場合は特別な処理（ユーザーによるキャンセルまたはコンポーネントアンマウント）
    if (error instanceof Error && error.name === 'AbortError') {
      logger.debug(`API request aborted [${method} ${path}]:`, {
        requestId,
        reason: 'Request was cancelled (user action or component unmount)',
        duration,
      })

      // AbortErrorはそのまま再スローし、エラーログは出力しない
      throw error
    }

    // エラー時のパフォーマンス記録
    enhancedLogger.performance(`API ${method} ${path} (ERROR)`, duration, {
      requestId,
      error: error instanceof Error ? error.message : String(error),
    })

    logger.error(`API request failed [${method} ${path}]:`, error)
    throw error
  }
}

export const apiFetcher: SecureApiFetcher = {
  // 既存のAPIインターフェース（後方互換性維持）
  get: async <T>(path: string, config?: RequestConfig): ApiResponse<T> => {
    return secureRequest<T>('GET', path, undefined, config)
  },

  post: async <T>(path: string, body?: unknown, config?: RequestConfig): ApiResponse<T> => {
    return secureRequest<T>('POST', path, body, config)
  },

  put: async <T>(path: string, body?: unknown, config?: RequestConfig): ApiResponse<T> => {
    return secureRequest<T>('PUT', path, body, config)
  },

  patch: async <T>(path: string, body?: unknown, config?: RequestConfig): ApiResponse<T> => {
    return secureRequest<T>('PATCH', path, body, config)
  },

  delete: async <T>(path: string, config?: RequestConfig): ApiResponse<T> => {
    return secureRequest<T>('DELETE', path, config?.body, config)
  },

  // セキュリティ強化されたAPIインターフェース
  secureGet: async <T>(path: string, config?: SecureRequestConfig): ApiResponse<T> => {
    return secureRequest<T>('GET', path, undefined, config)
  },

  securePost: async <T>(
    path: string,
    body?: unknown,
    config?: SecureRequestConfig
  ): ApiResponse<T> => {
    return secureRequest<T>('POST', path, body, config)
  },

  securePut: async <T>(
    path: string,
    body?: unknown,
    config?: SecureRequestConfig
  ): ApiResponse<T> => {
    return secureRequest<T>('PUT', path, body, config)
  },

  securePatch: async <T>(
    path: string,
    body?: unknown,
    config?: SecureRequestConfig
  ): ApiResponse<T> => {
    return secureRequest<T>('PATCH', path, body, config)
  },

  secureDelete: async <T>(path: string, config?: SecureRequestConfig): ApiResponse<T> => {
    return secureRequest<T>('DELETE', path, config?.body, config)
  },
}

// For backward compatibility
export const fetchApi = apiFetcher
